import React, { useState, useEffect } from 'react';
import Chart from "react-apexcharts";
import { Dashboard_data, MediaUrl, List_country, List_city } from '../../Utils/Api';

const PlatformPerformance = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [filters, setFilters] = useState({
        location_id: '',
        start_date: '',
        end_date: ''
    });

    useEffect(() => {
        fetchCountries();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchCountries = async () => {
        try {
            const response = await List_country();
            if (response && response.data?.data) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    const fetchCities = async (countryId) => {
        try {
            const response = await List_city(countryId);
            if (response && response.data?.data) {
                setCities(response.data.data);
            } else {
                setCities([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            setCities([]);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Remove empty filters before sending
            const params = {};
            if (filters.location_id) params.location_id = filters.location_id;
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;

            const response = await Dashboard_data(params);
            if (response.data) {
                setData(response.data?.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCountryChange = (e) => {
        const countryId = e.target.value;
        setSelectedCountry(countryId);
        setFilters(prev => ({ ...prev, location_id: '' })); // Clear city selection
        if (countryId) {
            fetchCities(countryId);
        } else {
            setCities([]);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const clearFilters = () => {
        setFilters({
            location_id: '',
            start_date: '',
            end_date: ''
        });
        setSelectedCountry('');
        setCities([]);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!data) return <div>No Data Available</div>;

    const { graph_metrics, regional_metrics } = data || {};

    // Prepare chart data (Last 10 records)
    const skillstersData = (graph_metrics?.skillsters_growth || []).slice(-10);
    const gigstersData = (graph_metrics?.gigsters_growth || []).slice(-10);
    const revenueData = (graph_metrics?.revenue_over_time || []).slice(-10);

    // Determine Categories for Growth Chart


    // Charts Configuration
    // Charts Configuration
    const commonOptions = {
        chart: { type: 'area', height: 350, toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
            type: 'category',
            labels: {
                formatter: function (value) {
                    const date = new Date(value);
                    return !isNaN(date.getTime())
                        ? date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                        : value;
                }
            }
        },
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.9,
                stops: [0, 90, 100]
            }
        }
    };

    const skillsterOptions = {
        ...commonOptions,
        colors: ['#00E396'], // Green for Skillsters
        xaxis: { ...commonOptions.xaxis, categories: skillstersData.map(item => item._id) }
    };

    const gigsterOptions = {
        ...commonOptions,
        colors: ['#008FFB'], // Blue for Gigsters
        xaxis: { ...commonOptions.xaxis, categories: gigstersData.map(item => item._id) }
    };

    const skillsterSeries = [{
        name: 'Skillsters',
        data: skillstersData.map(item => item.count || item.skillsters || 0)
    }];

    const gigsterSeries = [{
        name: 'Gigsters',
        data: gigstersData.map(item => item.count || item.gigsters || 0)
    }];

    const revenueOptions = {
        chart: { type: 'bar', height: 350 },
        xaxis: {
            categories: revenueData.map(item => item._id),
            type: 'category',
            labels: {
                formatter: function (value) {
                    const date = new Date(value);
                    return !isNaN(date.getTime())
                        ? date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
                        : value;
                }
            }
        },
        yaxis: {
            labels: {
                formatter: function (value) {
                    return value.toFixed(0);
                }
            }
        },
        colors: ['#775DD0'],
        plotOptions: { bar: { borderRadius: 4 } }
    };

    const revenueSeries = [{
        name: 'Revenue',
        data: revenueData.map(item => item.revenue || 0)
    }];

    return (
        <div>
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                <h1 className="page-title fw-semibold fs-18 mb-0">Platform Performance</h1>
            </div>

            {/* Filters */}
            <div className="card custom-card">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Country</label>
                            <select
                                className="form-select"
                                value={selectedCountry}
                                onChange={handleCountryChange}
                            >
                                <option value="">Select Country</option>
                                {countries.map(country => (
                                    <option key={country._id} value={country._id}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">City (Location)</label>
                            <select
                                className="form-select"
                                name="location_id"
                                value={filters.location_id}
                                onChange={handleFilterChange}
                                disabled={!selectedCountry}
                            >
                                <option value="">All Cities</option>
                                {cities.map(city => (
                                    <option key={city._id} value={city._id}>
                                        {city.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="start_date"
                                value={filters.start_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                name="end_date"
                                value={filters.end_date}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button
                                className="btn btn-outline-danger w-100"
                                onClick={clearFilters}
                            >
                                <i className="bx bx-reset me-1"></i> Reset
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Growth Charts Row */}
            <div className="row">
                <div className="col-xl-6">
                    <div className="card custom-card">
                        <div className="card-header">
                            <div className="card-title">Skillster Growth</div>
                        </div>
                        <div className="card-body">
                            <Chart options={skillsterOptions} series={skillsterSeries} type="area" height={350} />
                        </div>
                    </div>
                </div>
                <div className="col-xl-6">
                    <div className="card custom-card">
                        <div className="card-header">
                            <div className="card-title">Gigster Growth</div>
                        </div>
                        <div className="card-body">
                            <Chart options={gigsterOptions} series={gigsterSeries} type="area" height={350} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Revenue Row */}
            <div className="row">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-header">
                            <div className="card-title">Revenue Over Time</div>
                        </div>
                        <div className="card-body">
                            <Chart options={revenueOptions} series={revenueSeries} type="bar" height={350} />
                        </div>
                    </div>
                </div>
            </div>



            {/* Regional Metrics */}
            <div className="row">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-header">
                            <div className="card-title">Regional Distribution</div>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table text-nowrap table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Region</th>
                                            <th>Total Users</th>
                                            <th>Skillsters</th>
                                            <th>Gigsters</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(regional_metrics?.users_by_region || [])
                                            .sort((a, b) => b.count - a.count)
                                            // .slice(0, 5)
                                            .map((region, index) => (
                                                <tr key={index}>
                                                    <td>{region.name || "Unknown"}</td>
                                                    <td>{region.count}</td>
                                                    <td>{region.skillsters}</td>
                                                    <td>{region.gigsters}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

    );
};

export default PlatformPerformance;
